import {
	type AuthenticationResult,
	InteractionStatus,
	InteractionType,
	type SilentRequest,
} from "@azure/msal-browser";
import {
	useIsAuthenticated,
	useMsal,
	useMsalAuthentication,
} from "@azure/msal-react";
import { Stack } from "@mui/material";
import { navigate } from "gatsby";
import React, { type ComponentType, useEffect } from "react";

import { type PostLoginResponse } from "@repo/api";
import { EnvironmentHelper, readEnv } from "@repo/helpers";
import { LocalStorageKeys } from "@repo/logic";
import { cs } from "@repo/ui";
import { DrawerManager } from "../components/DrawerManager";
import { useIsUserLoggedIn } from "../hooks/useIsUserLoggedIn";
import { usePostLogin } from "../queryHooks";

const isV2RegistrationEnabled =
	readEnv("GATSBY_FEATURE_IS_V2_REGISTRATION_ENABLED") === "true";

const getAuthRequest = (auth: string | null): SilentRequest => {
	const request: SilentRequest = {
		scopes: ["openid", readEnv("GATSBY_B2C_API_SCOPE") ?? ""],
		redirectUri: "",
		authority: auth ?? undefined,
	};
	if (auth) request.authority = auth;
	return request;
};

const setupInitialConsentDrawer = (
	data: PostLoginResponse,
	isAirBank: boolean
) => {
	if (data.startElectronicContactsConfirmation && !isAirBank)
		navigate("/jsou-vase-udaje-spravne");
};

const getIsUserNotFound = (result: AuthenticationResult): boolean => {
	if (result.authority.includes("_change")) return false;
	if (!result.account.idTokenClaims) return false;

	// Kontrola extension fieldu pouze pokud je V2 disabled
	if (!isV2RegistrationEnabled) {
		return (
			!result.account.idTokenClaims.extension_PID ||
			!result.account.idTokenClaims.extension_UserDetailInfoEncrypted
		);
	}

	return false;
};

// Gets last token issuer url
const getLastUsedAuthority = () => {
	return EnvironmentHelper.isBrowser()
		? localStorage.getItem(LocalStorageKeys.LAST_USED_AUTHORITY)
		: null;
};

export default function withProtection<T extends React.JSX.IntrinsicAttributes>(
	PageComponent: ComponentType<T>,
	url?: string
): (props: T) => JSX.Element {
	return function wrapperWithProtection(props: T): JSX.Element {
		const lastUsedAuthority = getLastUsedAuthority();
		const { inProgress: inProgressMsal } = useMsal();
		const isAuth = useIsAuthenticated();
		const { login, result } = useMsalAuthentication(
			InteractionType.Redirect,
			getAuthRequest(lastUsedAuthority)
		);
		const {
			data: postLoginData,
			isLoading: isLoadingPostLogin,
			isSuccess: isSuccessPostLogin,
			isFetching: isFetchingPostLogin,
		} = usePostLogin();
		const isLoggedUser = useIsUserLoggedIn();

		const isPID = result?.account.idTokenClaims?.extension_PID;
		const isAirBank = result?.account.idTokenClaims?.partner_code === "AirBank";

		useEffect(() => {
			if (
				inProgressMsal === InteractionStatus.None &&
				result &&
				getIsUserNotFound(result)
			) {
				navigate("/uzivatel-nenalezen/");
			}
		}, [inProgressMsal, result]);

		useEffect(() => {
			// attempt silent login to refresh token
			if (!isAuth && inProgressMsal === InteractionStatus.None) {
				if (lastUsedAuthority) {
					// Use catch to handle any errors during login attempt
					void login(
						InteractionType.Redirect,
						getAuthRequest(lastUsedAuthority)
					).catch((error: unknown) => {
						// Only navigate to rozcestnik if it's not an interaction_in_progress error
						if (
							typeof error === "object" &&
							error !== null &&
							"errorCode" in error
						) {
							const msalError = error as { errorCode?: string };
							if (msalError.errorCode !== "interaction_in_progress") {
								navigate("/rozcestnik/");
							}
						} else {
							navigate("/rozcestnik/");
						}
					});
				} else {
					navigate("/rozcestnik/");
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps -- Dependencies are intentionally limited to isAuth and inProgress
		}, [isAuth, inProgressMsal]);

		// redirect to "/jsou-vase-udaje-spravne" but not on LOCAL (DEBUG) environment
		useEffect(() => {
			if (isPID && isSuccessPostLogin && !isFetchingPostLogin) {
				setupInitialConsentDrawer(postLoginData, isAirBank);
			}
		}, [
			isPID,
			isSuccessPostLogin,
			isFetchingPostLogin,
			isAirBank,
			postLoginData,
		]);

		// redirect to "/dokonceni-registrace" when isRegistrationCompleted is false
		useEffect(() => {
			if (
				isV2RegistrationEnabled &&
				postLoginData?.isRegistrationCompleted === false
			) {
				navigate("/dokonceni-registrace/");
			}
		}, [postLoginData?.isRegistrationCompleted]);

		if (
			!postLoginData ||
			(isV2RegistrationEnabled &&
				postLoginData.isRegistrationCompleted === false) ||
			inProgressMsal !== InteractionStatus.None ||
			!result ||
			getIsUserNotFound(result) ||
			isLoadingPostLogin ||
			(postLoginData.startElectronicContactsConfirmation &&
				url &&
				url !== "jsou-vase-udaje-spravne" &&
				url !== "uvitaci-obrazovka-airbank")
		) {
			return (
				<Stack sx={{ px: { md: 4, xs: 0 } }}>{cs.global.pageIsLoading}</Stack>
			);
		}

		return (
			<>
				{isLoggedUser && (
					<>
						<PageComponent {...props} />
						<DrawerManager
							disableAgreementDrawer={
								url === "uvitaci-obrazovka-airbank" ||
								EnvironmentHelper.isLocal()
							}
							disableAmlAndBankIdDrawers={
								readEnv("GATSBY_FEATURE_IS_AML_AND_BANK_ID_ENABLED") !== "true"
							}
						/>
					</>
				)}
			</>
		);
	};
}
